import React, {
  lazy,
  Suspense,
  memo,
  useEffect,
  useState,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { ConfigProvider } from "antd";
import { io } from "socket.io-client";
import { persistor } from "../redux/store/index";
import Loading from "components/shared-components/Loading";
import { lightTheme, darkTheme } from "configs/ThemeConfig";
import { resources } from "lang";
import useBodyClass from "utils/hooks/useBodyClass";
import Routes from "routes";
import { useTranslation } from "react-i18next";
import {
  signOut,
  resetApp,
  fetchGlobalSettings,
} from "redux/store/slices/authSlice";
import { jwtDecode } from "jwt-decode";
import Footer from "components/layout-components/Footer";
import { AUTH_PREFIX_PATH, PORTAL_API_BASE } from "configs/AppConfig";
import { onLocaleChange } from "redux/store/slices/themeSlice";

const AppLayout = lazy(() => import("./AppLayout"));
const AuthLayout = lazy(() => import("./AuthLayout"));
const Maintenance = lazy(() => import("views/public-views/maintenance"));

const validLanguages = ["en", "es", "fr"];
const getValidLanguage = (lng) => {
  return validLanguages.includes(lng) ? lng : "en";
};

const Layouts = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const blankLayout = useSelector((state) => state.theme.blankLayout);

  const [maintenanceMode, setMaintenanceMode] = useState(null); // null = loading
  const socketRef = useRef(null);

  const locale = useSelector((state) => state.theme.locale);
  const direction = useSelector((state) => state.theme.direction);
  const currentTheme = useSelector((state) => state.theme.currentTheme);
  const [antdLocale, setAntdLocale] = useState(
    resources[getValidLanguage(i18n.language)].antd,
  );
  const currentAppLocale = resources[locale];

  useBodyClass(`dir-${direction}`);

  const themeConfig =
    currentTheme === "light" ? { ...lightTheme } : { ...darkTheme };

  const Layout = token && !blankLayout ? AppLayout : AuthLayout;

  // ---------------- Initialize i18n & AntD locale on mount ----------------
  useEffect(() => {
    const lang = getValidLanguage(i18n.language || "en");
    i18n.changeLanguage(lang);
    setAntdLocale(resources[lang].antd);
  }, [i18n]);

  // ---------------- Update AntD locale whenever i18n language changes ----------------
  useEffect(() => {
    const handleLanguageChange = (lng) =>
      setAntdLocale(resources[getValidLanguage(lng)].antd);
    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  // ---------------- Sync Redux locale with i18n ----------------
  useEffect(() => {
    const lang = getValidLanguage(locale);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [locale, i18n]);

  // ---------------- Token validation ----------------
  useEffect(() => {
    async function handleRedirect() {
      if (!token) {
        dispatch(signOut());
        dispatch(resetApp());
        await persistor.purge();
        return;
      }
      try {
        const decodedToken = jwtDecode(token);
        if (
          !(
            decodedToken.hasOwnProperty("type") &&
            decodedToken.hasOwnProperty("role")
          )
        ) {
          dispatch(signOut());
          dispatch(resetApp());
          await persistor.purge();
        }
      } catch (err) {
        dispatch(signOut());
        dispatch(resetApp());
        await persistor.purge();
      }
    }
    handleRedirect();
  }, [token, dispatch]);

  // ---------------- Fetch initial maintenance flag ----------------
  useEffect(() => {
    async function getInitialMaintenance() {
      const response = await dispatch(
        fetchGlobalSettings({ type: "globalSettings" }),
      );
      const { success } = response.payload;
      if (success) {
        const { data } = response.payload;
        setMaintenanceMode(Boolean(data && data[0] && data[0].maintenanceMode));
      } else {
        setMaintenanceMode(false); // fallback
      }
    }
    getInitialMaintenance();
  }, [dispatch]);

  // ---------------- Socket connection ----------------
  useEffect(() => {
    socketRef.current = io(PORTAL_API_BASE, {
      path: "/socket.io/",
      transports: ["websocket"],
    });

    socketRef.current.on("connect", () => {
      console.log("✅ Connected to maintenance socket");
    });

    socketRef.current.on("maintenanceUpdate", (isMaintenanceOn) => {
      console.log("⚙️ Maintenance mode changed:", isMaintenanceOn);
      setMaintenanceMode(isMaintenanceOn);

      if (isMaintenanceOn) {
        dispatch(signOut());
        dispatch(resetApp());
        persistor.purge();
      }
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [dispatch]);

  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale]);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      if (event.key === "appLanguage") {
        dispatch(onLocaleChange(event.newValue));
        i18n.changeLanguage(event.newValue);
      }
    };

    window.addEventListener("storage", handleLanguageChange);

    return () => {
      window.removeEventListener("storage", handleLanguageChange);
    };
  }, [dispatch, i18n]);

  // ---------------- Block rendering until maintenance flag is known ----------------
  if (maintenanceMode === null) {
    return (
      <ConfigProvider
        theme={themeConfig}
        direction={direction}
        locale={antdLocale}
      >
        <Suspense fallback={<Loading cover="content" />} />
      </ConfigProvider>
    );
  }

  // ---------------- Show maintenance page if active ----------------
  if (maintenanceMode) {
    return (
      <ConfigProvider
        theme={themeConfig}
        direction={direction}
        locale={antdLocale}
      >
        <Suspense fallback={<Loading cover="content" />}>
          <Maintenance />
          <Footer baseUrl={AUTH_PREFIX_PATH} maintenance={true} />
        </Suspense>
      </ConfigProvider>
    );
  }

  // ---------------- Normal layout rendering ----------------
  return (
    <ConfigProvider
      theme={themeConfig}
      direction={direction}
      locale={antdLocale}
    >
      <Suspense fallback={<Loading cover="content" />}>
        <Layout>
          <Routes />
        </Layout>
      </Suspense>
    </ConfigProvider>
  );
};

export default memo(Layouts);
